/**
 * SidePanel — right-side drawer (premium, quiet).
 *
 * - Fixed width 480px on >= sm, full-width on mobile.
 * - Sticky header with title + optional subtitle and close button.
 * - Esc closes. Click on backdrop closes. Body scroll is locked while open.
 * - Content area scrolls independently. Optional sticky footer.
 * - Safe areas: the header adds env(safe-area-inset-top) to its top
 *   padding (so the close X and headerRight never sit under the iOS
 *   status bar / notch — zero on desktop, i.e. unchanged) and the footer
 *   keeps env(safe-area-inset-bottom). No device-specific pixels.
 *
 * Usage:
 *   <SidePanel open={!!lead} onClose={() => setLead(null)}
 *              title={lead?.name} subtitle="Lead">
 *     ...sections...
 *   </SidePanel>
 */
import React, { useEffect } from "react";
import { X } from "@phosphor-icons/react";
import { acquireBodyScrollLock } from "@/lib/bodyScrollLock";

export default function SidePanel({
  open,
  onClose,
  title,
  subtitle,
  headerRight,
  footer,
  children,
  testId = "side-panel",
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const releaseScrollLock = acquireBodyScrollLock();
    return () => {
      document.removeEventListener("keydown", onKey);
      releaseScrollLock();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid={testId}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-stone-900/30 backdrop-blur-[1px] animate-[fadeIn_120ms_ease-out]"
        onClick={onClose}
      />
      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-2xl flex flex-col animate-[slideInRight_200ms_ease-out]"
      >
        <header className="flex items-start justify-between gap-3 px-6 pb-4 pt-[calc(1rem_+_env(safe-area-inset-top,0px))] border-b border-stone-200 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            {subtitle && (
              <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-0.5">
                {subtitle}
              </div>
            )}
            <div className="font-serif-dv text-2xl truncate" title={title}>{title}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerRight}
            <button
              onClick={onClose}
              data-testid={`${testId}-close`}
              aria-label="Cerrar"
              className="w-11 h-11 sm:w-8 sm:h-8 rounded-full hover:bg-stone-100 flex items-center justify-center text-stone-500 hover:text-stone-900 transition"
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {children}
        </div>
        {footer && (
          <footer
            className="border-t border-stone-200 px-6 pt-3 bg-white sticky bottom-0"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
          >
            {footer}
          </footer>
        )}
      </aside>

      {/* Local keyframes (avoid touching global CSS) */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideInRight { from { transform: translateX(24px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
      `}</style>
    </div>
  );
}
